import { type CSSProperties, Fragment, type ReactNode } from "react";

/**
 * This is a verbatim copy of `src/components/app/markdown.tsx`, not an
 * import. `scripts/deploy-hub.sh` rsyncs `hub/` alone onto the box, so a
 * symlink or a cross-package import out of `hub/` would dangle at deploy
 * time -- that script exists precisely because an earlier version of this
 * console shipped with no styling for exactly that reason. A ~300-line
 * duplicate is the honest cost of the hub being separately deployable.
 *
 * Any behaviour change made here belongs in both copies.
 */

/**
 * Minimal, safe markdown rendering for assistant replies. Agents routinely
 * answer in markdown (headings, **bold**, `code`, fenced blocks, lists,
 * tables), and a busy exec should see it formatted — not raw `**`, ``` fences,
 * or `|` pipes. This is a tiny purpose-built renderer rather than a
 * dependency: it covers the block + inline constructs the agent actually
 * emits and, critically, never dangerously sets HTML — every node is a real
 * React element, so there is no XSS surface (an agent reply is untrusted
 * content by the time it reaches the browser).
 *
 * Deliberately NOT a full CommonMark implementation. Supported:
 *   Blocks:  # / ## / ### headings, fenced ``` code blocks, - / * / 1. lists,
 *            | GFM | pipe | tables |, blank-line-separated paragraphs.
 *   Inline:  **bold**, *italic* / _italic_, `code`, [text](url) links.
 * Anything else renders as its literal text (safe fallback), so an unusual
 * construct degrades to plain text rather than breaking.
 */

type Align = "left" | "center" | "right" | null;

type Block =
  | { type: "heading"; level: 1 | 2 | 3; text: string }
  | { type: "code"; lang: string | null; code: string }
  | { type: "list"; ordered: boolean; start: number; items: string[] }
  | { type: "table"; header: string[]; align: Align[]; rows: string[][] }
  | { type: "paragraph"; text: string };

const HEADING_RE = /^(#{1,3})\s+(.*)$/;
const FENCE_RE = /^```(.*)$/;
// Group 1 is the marker, group 2 the item text — the same shape for both so
// the parse loop reads one index. Group 1 is only meaningful for OL, where it
// is the literal number the author wrote.
const UL_ITEM_RE = /^([-*])\s+(.*)$/;
const OL_ITEM_RE = /^(\d+)\.\s+(.*)$/;
const TABLE_SEPARATOR_CELL_RE = /^:?-+:?$/;

/**
 * Split a pipe-table row into its cell strings. Handles an optional leading
 * and/or trailing `|` (both are conventional in GFM tables but not required)
 * and lets `\|` escape a literal pipe inside a cell.
 */
function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);

  const cells: string[] = [];
  let cur = "";
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === "\\" && s[i + 1] === "|") {
      cur += "|";
      i++;
    } else if (ch === "|") {
      cells.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  cells.push(cur.trim());
  return cells;
}

/**
 * If `line` is a GFM table alignment separator (e.g. `| --- | :--: | --: |`),
 * return its cells; otherwise `null`. Returning the cells (instead of a bare
 * boolean) lets callers reuse this single parse for both "is this a
 * separator row" and "what does it say about column alignment" — the row
 * doesn't get tokenized twice.
 *
 * Exported so callers that slice markdown before rendering it (card previews)
 * can ask this module — rather than a second, drifting copy of the rule —
 * whether a line is the half of a table that must not be cut away.
 */
export function parseTableSeparator(line: string): string[] | null {
  const trimmed = line.trim();
  if (!trimmed.includes("-") || !trimmed.includes("|")) return null;
  const cells = splitTableRow(trimmed);
  return cells.length > 0 && cells.every((c) => TABLE_SEPARATOR_CELL_RE.test(c)) ? cells : null;
}

function parseAlign(separatorCell: string): Align {
  const left = separatorCell.startsWith(":");
  const right = separatorCell.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  if (left) return "left";
  return null;
}

/**
 * True if `lines[i]` begins a new block — blank, fence, heading, list item,
 * or a table (a line with a `|` whose next line is an alignment separator).
 * Used as the *stop* condition for both the paragraph-gathering loop and the
 * table row-continuation loop, so a table or list that starts right after
 * (no blank line) is never swallowed into the block above it.
 */
function startsNewBlock(lines: string[], i: number): boolean {
  const line = lines[i] ?? "";
  if (line.trim() === "") return true;
  if (FENCE_RE.test(line)) return true;
  if (HEADING_RE.test(line)) return true;
  if (UL_ITEM_RE.test(line) || OL_ITEM_RE.test(line)) return true;
  if (line.includes("|")) {
    const next = lines[i + 1];
    if (next !== undefined && parseTableSeparator(next) !== null) return true;
  }
  return false;
}

/** Parse markdown source into a flat list of blocks. Pure — exported for tests. */
export function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";

    // Fenced code block — capture verbatim until the closing fence.
    const fence = FENCE_RE.exec(line);
    if (fence) {
      const lang = fence[1]?.trim() || null;
      const code: string[] = [];
      i++;
      while (i < lines.length && !FENCE_RE.test(lines[i] ?? "")) {
        code.push(lines[i] ?? "");
        i++;
      }
      i++; // consume closing fence (or run off the end if unterminated)
      blocks.push({ type: "code", lang, code: code.join("\n") });
      continue;
    }

    // Blank line — paragraph separator.
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Heading.
    const heading = HEADING_RE.exec(line);
    if (heading) {
      const level = heading[1]?.length as 1 | 2 | 3;
      blocks.push({ type: "heading", level, text: heading[2] ?? "" });
      i++;
      continue;
    }

    // List — a run of consecutive item lines of the same kind.
    const isUl = UL_ITEM_RE.test(line);
    const isOl = OL_ITEM_RE.test(line);
    if (isUl || isOl) {
      const ordered = isOl;
      const re = ordered ? OL_ITEM_RE : UL_ITEM_RE;
      const items: string[] = [];
      // A run ends at the first line that is not an item, so one logical
      // numbered list split by a code fence or a paragraph arrives here as
      // several blocks. Remember where this run actually started, or every
      // block after the first renders from "1." again.
      let start = 1;
      while (i < lines.length && re.test(lines[i] ?? "")) {
        const m = re.exec(lines[i] ?? "");
        if (ordered && items.length === 0) {
          const parsed = Number(m?.[1]);
          if (Number.isSafeInteger(parsed)) start = parsed;
        }
        items.push(m?.[2] ?? "");
        i++;
      }
      blocks.push({ type: "list", ordered, start, items });
      continue;
    }

    // Table — a header row immediately followed by a `---` alignment row.
    if (line.includes("|")) {
      const sepCells = i + 1 < lines.length ? parseTableSeparator(lines[i + 1] ?? "") : null;
      if (sepCells) {
        const header = splitTableRow(line);
        const align = header.map((_, col) => parseAlign(sepCells[col] ?? ""));
        i += 2;
        const rows: string[][] = [];
        while (i < lines.length) {
          const rowLine = lines[i] ?? "";
          if (!rowLine.includes("|")) break;
          // A new block (another table's header, a list item, …) starting
          // right here ends this table instead of being swallowed as a row.
          if (startsNewBlock(lines, i)) break;
          rows.push(splitTableRow(rowLine));
          i++;
        }
        blocks.push({ type: "table", header, align, rows });
        continue;
      }
    }

    // Paragraph — gather until a blank line or a block-starting line.
    const para: string[] = [line];
    i++;
    while (i < lines.length && !startsNewBlock(lines, i)) {
      para.push(lines[i] ?? "");
      i++;
    }
    blocks.push({ type: "paragraph", text: para.join("\n") });
  }

  return blocks;
}

// Inline tokenizer: split on the first matching delimiter, recurse on the rest.
// Order matters — code spans win over emphasis so `**` inside a code span stays
// literal.
const INLINE_CODE_RE = /`([^`]+)`/;
const BOLD_RE = /\*\*([^*]+)\*\*/;
// Emphasis: `*text*` anywhere, but `_text_` only at word boundaries so an
// identifier like `my_var_name` is NOT rendered with an italic "var". Group 1
// is the asterisk body, group 2 the underscore body.
const ITALIC_RE = /\*([^*]+)\*|(?<![A-Za-z0-9])_([^_]+)_(?![A-Za-z0-9])/;
// Link URL allows one level of balanced parens so Wikipedia-style URLs like
// `.../Foo_(bar)` are captured whole; the link's own closing `)` still ends it.
const LINK_RE = /\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))*)\)/;

// Only these schemes may become a live anchor. React does NOT sanitize href, so
// a raw `javascript:` / `data:` / `vbscript:` URL from an (untrusted) agent
// reply is click-to-XSS. Everything else renders as plain text.
const SAFE_URL_SCHEMES = ["http://", "https://", "mailto:"];

/**
 * Return a safe href for a markdown link, or `null` if the URL must NOT become
 * an anchor (render it as plain text instead). Scheme detection runs on a copy
 * with every space/control char removed, so obfuscation like `java\tscript:` or
 * a leading newline/null byte can't smuggle a disallowed scheme past the
 * allowlist — that stripping mirrors what a browser does before it parses the
 * URL. Relative and protocol-relative URLs also return `null`: an agent reply
 * has no meaningful base to resolve them against. Exported for tests.
 */
// All chars <= 0x20 (space, tab, CR, LF, null, ...) and 0x7F (DEL). Constructed
// from a string on purpose: a regex *literal* with these escapes trips Biome's
// noControlCharactersInRegex, and a literal with the raw bytes is unreadable —
// the string form sidesteps both, so useRegexLiterals is suppressed here.
// biome-ignore lint/complexity/useRegexLiterals: literal form conflicts with noControlCharactersInRegex
const URL_STRIP_RE = new RegExp("[\\u0000-\\u0020\\u007f]", "g");
export function safeHref(url: string): string | null {
  const cleaned = url.replace(URL_STRIP_RE, "");
  if (SAFE_URL_SCHEMES.some((scheme) => cleaned.toLowerCase().startsWith(scheme))) {
    return cleaned;
  }
  return null;
}

function alignStyle(align: Align): CSSProperties | undefined {
  return align ? { textAlign: align } : undefined;
}

/** Render inline markdown (bold/italic/code/links) within a text run. */
export function renderInline(text: string, keyPrefix = "i"): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let k = 0;

  while (rest.length > 0) {
    const code = INLINE_CODE_RE.exec(rest);
    const bold = BOLD_RE.exec(rest);
    const italic = ITALIC_RE.exec(rest);
    const link = LINK_RE.exec(rest);

    // Pick the earliest-occurring construct.
    const candidates = [
      code && { kind: "code" as const, m: code, at: code.index },
      bold && { kind: "bold" as const, m: bold, at: bold.index },
      italic && { kind: "italic" as const, m: italic, at: italic.index },
      link && { kind: "link" as const, m: link, at: link.index },
    ].filter((c): c is NonNullable<typeof c> => c !== null);

    if (candidates.length === 0) {
      out.push(rest);
      break;
    }

    const first = candidates.reduce((a, b) => (b.at < a.at ? b : a));
    const before = rest.slice(0, first.at);
    if (before) out.push(before);
    const key = `${keyPrefix}-${k++}`;

    if (first.kind === "code") {
      out.push(
        <code key={key} className="np-md-code">
          {first.m[1]}
        </code>,
      );
    } else if (first.kind === "bold") {
      out.push(<strong key={key}>{renderInline(first.m[1] ?? "", key)}</strong>);
    } else if (first.kind === "italic") {
      const inner = first.m[1] ?? first.m[2] ?? "";
      out.push(<em key={key}>{renderInline(inner, key)}</em>);
    } else {
      const href = safeHref(first.m[2] ?? "");
      if (href) {
        out.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {first.m[1]}
          </a>,
        );
      } else {
        // Disallowed/unsafe URL (javascript:, data:, relative, …) — never emit a
        // live anchor. Show the label and the URL as plain text so nothing is
        // hidden but nothing is clickable.
        out.push(`${first.m[1]} (${first.m[2]})`);
      }
    }

    rest = rest.slice(first.at + first.m[0].length);
  }

  return out;
}

/**
 * Markdown — renders an assistant reply body as formatted React. User messages
 * stay plain text (they typed it; no need to interpret their `*`).
 */
export function Markdown({ source }: { source: string }) {
  const blocks = parseBlocks(source);
  return (
    <div className="np-md">
      {blocks.map((block, idx) => {
        const key = `b-${idx}`;
        switch (block.type) {
          case "heading": {
            const Tag = `h${block.level}` as "h1" | "h2" | "h3";
            return (
              <Tag key={key} className={`np-md-h np-md-h${block.level}`}>
                {renderInline(block.text, key)}
              </Tag>
            );
          }
          case "code":
            return (
              <pre key={key} className="np-md-pre">
                <code>{block.code}</code>
              </pre>
            );
          case "list": {
            const items = block.items.map((item, j) => (
              // Static, never-reordered list parsed fresh each render — index key is safe.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional list, no reorder
              <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
            ));
            // `start` only on <ol> — it is not a valid attribute on <ul>. A run
            // that begins at "3." renders from 3, so a numbered list interrupted
            // by a code fence keeps counting instead of restarting.
            return block.ordered ? (
              <ol key={key} className="np-md-list" start={block.start}>
                {items}
              </ol>
            ) : (
              <ul key={key} className="np-md-list">
                {items}
              </ul>
            );
          }
          case "table": {
            return (
              <div key={key} className="np-md-table-wrap">
                <table className="np-md-table">
                  <thead>
                    <tr>
                      {block.header.map((cell, j) => (
                        // Static header parsed fresh each render — index key is safe.
                        // biome-ignore lint/suspicious/noArrayIndexKey: positional column, no reorder
                        <th key={`${key}-h-${j}`} style={alignStyle(block.align[j] ?? null)}>
                          {renderInline(cell, `${key}-h-${j}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, ri) => (
                      // Static, never-reordered rows parsed fresh each render — index key is safe.
                      // biome-ignore lint/suspicious/noArrayIndexKey: positional row, no reorder
                      <tr key={`${key}-r-${ri}`}>
                        {block.header.map((_, ci) => {
                          const cellKey = `${key}-r-${ri}-${ci}`;
                          return (
                            <td key={cellKey} style={alignStyle(block.align[ci] ?? null)}>
                              {renderInline(row[ci] ?? "", cellKey)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          default:
            return (
              <p key={key} className="np-md-p">
                {block.text.split("\n").map((ln, j, arr) => (
                  // Static soft-wrapped lines within one paragraph — index key is safe.
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional lines, no reorder
                  <Fragment key={`${key}-${j}`}>
                    {renderInline(ln, `${key}-${j}`)}
                    {j < arr.length - 1 ? <br /> : null}
                  </Fragment>
                ))}
              </p>
            );
        }
      })}
    </div>
  );
}
